/**
 * Cookie consent — the ONE format the browser writes and the server reads.
 *
 * Only deployments with `features.cookieConsent` (MiLicita, Spain — GDPR, LSSI-CE
 * art. 22.2 and the AEPD «Guía sobre el uso de las cookies») ever write or read
 * this cookie. UZ/KZ never do: nothing here runs unless that flag is on.
 *
 * Shape: `<version>.<savedAt unix seconds>.<analytics 0|1><marketing 0|1>`, e.g.
 * `1.1792400000.10` = version 1, analytics granted, marketing refused. Dots and
 * digits only, so it needs no encoding in a cookie and the inline bootstrap in
 * `index.html` (plain ES5, no bundle) can parse it with one regular expression.
 *
 * First-party and NOT httpOnly: the page reads it before the bundle loads (to
 * decide whether any tracker may start), and the API reads it on the requests
 * the browser makes with credentials (to decide whether a Meta CAPI event or a
 * first-party analytics event may be recorded for this visitor).
 */

/** The cookie that stores the visitor's choice. Technical (exempt) — it only
 *  remembers the choice the visitor made. */
export const CONSENT_COOKIE = 'tenders_consent';

/**
 * Bump when the categories, purposes or third parties change: every stored
 * choice of an older version is then treated as «no choice» and the banner asks
 * again (AEPD guía §3.2.7 «Cambios en el uso de las cookies»).
 */
export const CONSENT_VERSION = 1;

/**
 * How long a stored choice is honoured before the banner asks again. The AEPD
 * considers it good practice that a consent lasts no more than 24 months (guía
 * §3.2.8); 12 is the conservative half of that and is what the cookie policy
 * states. The cookie's own `max-age` is the same.
 */
export const CONSENT_MAX_AGE_DAYS = 365;

const DAY_MS = 24 * 60 * 60 * 1000;
/** A `savedAt` this far in the future is a clock skew we tolerate; beyond it the
 *  value is not believable and counts as no choice. */
const FUTURE_SKEW_MS = DAY_MS;

/** The two categories a visitor can grant. «Técnicas» are always on and are not
 *  stored — nothing can switch them off. */
export type ConsentCategory = 'analytics' | 'marketing';

export interface CookieConsent {
  readonly version: number;
  /** Unix seconds when the visitor made the choice. */
  readonly savedAt: number;
  /** PostHog (and GA4 where configured): «cookies analíticas». */
  readonly analytics: boolean;
  /** Meta Pixel + Conversions API: «cookies publicitarias». */
  readonly marketing: boolean;
}

export interface ConsentChoice {
  readonly analytics: boolean;
  readonly marketing: boolean;
}

const VALUE = /^(\d{1,4})\.(\d{1,12})\.([01])([01])$/;

/** The cookie VALUE for a choice made at `nowMs`. */
export function serializeConsent(choice: ConsentChoice, nowMs: number = Date.now()): string {
  const savedAt = Math.floor(nowMs / 1000);
  return `${CONSENT_VERSION}.${savedAt}.${choice.analytics ? 1 : 0}${choice.marketing ? 1 : 0}`;
}

/** Parse a cookie VALUE. Shape only — `isConsentCurrent` decides whether it still counts. */
export function parseConsent(raw: string | null | undefined): CookieConsent | null {
  if (!raw) return null;
  const m = VALUE.exec(raw.trim());
  if (!m) return null;
  return {
    version: Number(m[1]),
    savedAt: Number(m[2]),
    analytics: m[3] === '1',
    marketing: m[4] === '1',
  };
}

/** True when the stored choice is of the current version and younger than
 *  {@link CONSENT_MAX_AGE_DAYS}. Anything else means «ask again». */
export function isConsentCurrent(c: CookieConsent, nowMs: number = Date.now()): boolean {
  if (c.version !== CONSENT_VERSION) return false;
  const age = nowMs - c.savedAt * 1000;
  return age > -FUTURE_SKEW_MS && age < CONSENT_MAX_AGE_DAYS * DAY_MS;
}

/** The raw value of one cookie out of a `Cookie` header / `document.cookie`. */
function cookieValue(header: string, name: string): string | undefined {
  for (const part of header.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() === name) return part.slice(eq + 1).trim();
  }
  return undefined;
}

/**
 * The visitor's CURRENT choice from a `Cookie` header (server) or
 * `document.cookie` (browser), or `null` when there is none that still counts —
 * absent, malformed, an older version or expired. `null` means «no consent»:
 * every non-technical category is refused until the visitor chooses.
 */
export function readConsentCookie(
  cookieHeader: string | null | undefined,
  nowMs: number = Date.now(),
): CookieConsent | null {
  if (!cookieHeader) return null;
  const c = parseConsent(cookieValue(cookieHeader, CONSENT_COOKIE));
  return c && isConsentCurrent(c, nowMs) ? c : null;
}

/** Does this (possibly absent) choice grant `category`? No choice grants nothing. */
export function consentGrants(
  c: CookieConsent | null | undefined,
  category: ConsentCategory,
): boolean {
  return c?.[category] === true;
}

/**
 * The value `index.html`'s consent bootstrap is baked with (`__COOKIE_CONSENT__`):
 * `<cookie>:<version>:<max age days>`. One token, so the inline script and this
 * module cannot disagree about the name, the version or the lifetime.
 */
export function consentBootstrapToken(): string {
  return `${CONSENT_COOKIE}:${CONSENT_VERSION}:${CONSENT_MAX_AGE_DAYS}`;
}
